import React from 'react';
import KpiChartPanel from './KpiChartPanel';

export interface RateDonutItem {
  title: string;
  /** Numeric string; displayed with exactly 2 decimal places */
  value: string;
  /** Total tickets considered for this SLA metric */
  ticketCount?: number;
  drillKey?: string;
}

export interface KpiRateDonutsProps {
  /** Optional widget title (Screenshot 1 uses per-column headers only — omit for exact match) */
  title?: string;
  items: RateDonutItem[];
  onRateDrill?: (drillKey: string) => void;
}

const RING_STROKES_FALLBACK = ['rgb(99, 102, 241)', 'rgb(168, 85, 247)', 'rgb(14, 165, 233)'];

/** Case (blue); Ongoing Comm SLA = amber; Resolution SLA = purple (Incident + Request) */
const RING_COLOR_BY_DRILL_KEY: Record<string, string> = {
  case_response: 'rgb(30, 64, 175)',
  incident_ongoing: 'rgb(217, 119, 6)',
  incident_resolution: 'rgb(147, 51, 234)',
  request_ongoing: 'rgb(217, 119, 6)',
  request_resolution: 'rgb(147, 51, 234)',
};

function ringStrokeForItem(item: RateDonutItem, index: number): string {
  if (item.drillKey && RING_COLOR_BY_DRILL_KEY[item.drillKey]) {
    return RING_COLOR_BY_DRILL_KEY[item.drillKey];
  }
  return RING_STROKES_FALLBACK[index % RING_STROKES_FALLBACK.length];
}

/** SVG ring — no Chart.js so global datalabel plugins cannot leak onto the donut */
function RateRing({ percent, strokeColor }: { percent: number; strokeColor: string }) {
  const p = Math.min(100, Math.max(0, Number.isFinite(percent) ? percent : 0));
  const size = 132;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const dash = (p / 100) * circumference;
  const gap = Math.max(0, circumference - dash);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0 drop-shadow-sm"
      aria-hidden
    >
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={strokeColor}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${gap}`}
        transform={`rotate(-90 ${cx} ${cy})`}
        className="transition-[stroke-dasharray] duration-500 ease-out"
      />
    </svg>
  );
}

function formatRateTwoDecimals(value: string): string {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

function formatTicketCount(count?: number): string {
  const safe = Number.isFinite(count) ? Math.max(0, Number(count)) : 0;
  const unit = safe === 1 ? 'ticket' : 'tickets';
  return `${safe.toLocaleString()} ${unit}`;
}

const KpiRateDonuts: React.FC<KpiRateDonutsProps> = ({ title, items, onRateDrill }) => (
  <KpiChartPanel
    title={title}
    flexFill
    className="h-full"
    bodyClassName={title ? 'px-3 pb-4 pt-4' : 'p-0'}
  >
    <div className="grid min-h-[280px] flex-1 grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {items.map((item, idx) => {
        const pct = parseFloat(item.value);
        const display = formatRateTwoDecimals(item.value);
        const strokeColor = ringStrokeForItem(item, idx);
        const clickable = Boolean(item.drillKey && onRateDrill);
        const Inner = (
          <>
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/20 px-3 py-2.5 text-center">
              <span className="text-[13px] font-medium leading-snug text-slate-800">{item.title}</span>
            </div>
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-6">
              <div className="relative inline-flex items-center justify-center">
                <RateRing percent={pct} strokeColor={strokeColor} />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center">
                    <span className="text-lg font-bold tabular-nums text-slate-900">{display}%</span>
                    <span className="mt-0.5 text-[11px] font-medium text-slate-500">
                      {formatTicketCount(item.ticketCount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        );
        return (
          <div
            key={item.title}
            className="flex min-h-0 min-w-0 flex-1 flex-col bg-gradient-to-b from-white to-slate-50/50"
          >
            {clickable ? (
              <button
                type="button"
                onClick={() => onRateDrill!(item.drillKey!)}
                className="flex min-h-0 min-w-0 flex-1 flex-col text-left transition-colors hover:bg-indigo-50/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
              >
                {Inner}
              </button>
            ) : (
              Inner
            )}
          </div>
        );
      })}
    </div>
  </KpiChartPanel>
);

export default KpiRateDonuts;
